<script setup lang="ts">
import { IconGift } from '@tabler/icons-vue';
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';

import PublicPageLayout from '../../components/PublicPageLayout.vue';
import RequestStatus from '../../components/RequestStatus.vue';
import { useRequestBase } from '../../composables/request.ts';
import { usePublicFlowAdapter } from '../adapter.ts';
import FlowAuthentication from './FlowAuthentication.vue';

type Offer = { id: number; name: string; applied: boolean | null };

const route = useRoute();
const adapter = usePublicFlowAdapter();
const endpoint = computed(
    () => `${adapter.apiUrl}/referral-offers/${String(route.params.referralCode)}`,
);
const offerRequest = useRequestBase<Offer>(endpoint, { refetch: true }).json();

offerRequest.onFetchResponse((response) => {
    const applied = offerRequest.data.value?.applied;

    if (response.ok && applied) {
        void adapter.navigate('/');
    }
});

watch(adapter.actorEmail, (value) => {
    if (value) {
        void offerRequest.execute();
    }
});
</script>

<template>
    <RequestStatus :request="offerRequest">
        <PublicPageLayout
            v-if="offerRequest.data.value?.applied === null"
            :brand-url="adapter.brandUrl"
            :icon="adapter.brandUrl ? undefined : IconGift"
            :title="`${offerRequest.data.value.name} Sent You a Free Month!`"
        >
            <template #description>
                <p>
                    Start with a 5-day free trial and then purchase a subscription to claim your
                    offer.
                </p>
                <p class="text-xs wrap-balance">
                    This referral offer is only available when you create a new account using this
                    page.
                </p>
            </template>

            <FlowAuthentication
                :intended="route.fullPath"
                :registration="{ referrer_id: offerRequest.data.value.id }"
            />
        </PublicPageLayout>

        <PublicPageLayout
            v-else-if="offerRequest.data.value?.applied === false"
            :brand-url="adapter.brandUrl"
            title="Referral Offer Not Available"
        >
            <template #description>
                <p>
                    Referral offers can only be applied when creating a new account, but you are
                    already signed in to an existing account.
                </p>
                <button class="btn btn--lg bg-primary full-width" @click="adapter.back('/')">
                    Continue to eMedici
                </button>
            </template>
        </PublicPageLayout>
    </RequestStatus>
</template>
